import { LoaderCircle } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { SsoDivider } from '@/features/auth/components/SsoDivider'
import { ssoInviteStash, type InviteLanding } from '@/features/auth/lib/ssoInviteStash'
import {
  loginAuthorizeUrl,
  startInviteSso,
  type OAuthProviderId
} from '@/features/auth/services/oauthService.http'
import {
  loadOAuthProviders,
  selectOfferedOAuthProviders
} from '@/features/auth/slices/oauthProvidersSlice'
import { isApiError } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store'

/** Where the sign-in starts from: a plain `/login`, or an invite the page holds. */
export type SsoMode = { kind: 'login' } | { kind: 'invite'; token: string; landing?: InviteLanding }

interface SsoButtonsProps {
  mode: SsoMode
  /** Shown under the buttons, and only when at least one button renders. */
  hint?: ReactNode
  /** An invite start failed; the key is the backend's (or `oauth.generic`). */
  onError?: (key: string) => void
  /** A provider button was clicked — lets the page clear a stale alert. */
  onStart?: () => void
  /** Test seam; the default leaves the SPA for the provider. */
  redirect?: (url: string) => void
}

const leaveForProvider = (url: string): void => {
  // The provider (or the API that redirects to it) is outside the SPA, so this
  // is a real navigation, never `navigate()`.
  window.location.assign(url)
}

/**
 * "Continue with Google / Microsoft" (CR-SSO-INVITE-PX-2026-09-26), shared by
 * `/login` and `/accept-invite`. Adapted from xelerate's `OAuthButtons` (the
 * official marks, fail-closed rendering) and fksr's invite options (busy keeps
 * opacity, the hint that disappears with the buttons).
 *
 * It renders NOTHING — no divider, no hint, no disabled button — while the
 * provider list loads, when it fails, and when nothing is configured (AC-8,
 * AC-11): the divider and the hint are gated by this component's own structure,
 * not by a second check at each call site. The order is `OFFERED_PROVIDERS`
 * (Google, then Microsoft), never the server's.
 *
 * Busy: the clicked button gets `aria-busy` + `aria-disabled` (not `disabled`,
 * which would drop keyboard focus to the body) and the other is `disabled`; the
 * handler ignores clicks while a redirect is pending. At rest every button is
 * live.
 */
export function SsoButtons({ mode, hint, onError, onStart, redirect = leaveForProvider }: SsoButtonsProps) {
  const { t } = useTranslation('auth')
  const dispatch = useAppDispatch()
  const offered = useAppSelector(selectOfferedOAuthProviders)
  const [pending, setPending] = useState<OAuthProviderId | null>(null)

  useEffect(() => {
    void dispatch(loadOAuthProviders())
  }, [dispatch])

  // Back from the provider, Safari and Firefox restore this page from the
  // back/forward cache with `pending` still set: the clicked button would stay
  // busy and the other disabled until a reload. A persisted `pageshow` is that
  // restore (a normal load is not persisted), so it frees the buttons.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) setPending(null)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  if (offered.length === 0) return null

  const start = async (provider: OAuthProviderId): Promise<void> => {
    if (pending !== null) return
    setPending(provider)
    onStart?.()
    if (mode.kind === 'login') {
      // A plain sign-in carries no invite: drop any token an abandoned invite
      // start left behind, so a failure here returns to /login (review low A6).
      ssoInviteStash.clear()
      redirect(loginAuthorizeUrl(provider))
      return
    }
    try {
      const authorizeUrl = await startInviteSso(provider, mode.token)
      ssoInviteStash.put(mode.token, mode.landing)
      redirect(authorizeUrl)
    } catch (error) {
      setPending(null)
      onError?.((isApiError(error) ? error.backendKey : undefined) ?? 'oauth.generic')
    }
  }

  return (
    <section data-testid="sso-options" aria-label={t('sso.regionLabel')} className="mt-4">
      <SsoDivider />
      <div className="mt-4 grid gap-2">
        {offered.map((provider) => {
          const busy = pending === provider
          return (
            <Button
              key={provider}
              type="button"
              variant="outline"
              data-testid={`sso-${provider}`}
              aria-busy={busy || undefined}
              aria-disabled={busy || undefined}
              disabled={pending !== null && !busy}
              onClick={() => void start(provider)}
              className="h-auto min-h-9 w-full whitespace-normal max-sm:min-h-[44px] px-4 py-2 text-center focus-visible:ring-mint-ink focus-visible:ring-offset-2 focus-visible:ring-offset-card aria-busy:cursor-progress aria-busy:bg-muted"
            >
              {busy ? (
                <LoaderCircle aria-hidden className="size-4 shrink-0 text-muted-foreground motion-safe:animate-spin" />
              ) : (
                <ProviderMark provider={provider} />
              )}
              <span className="min-w-0 break-words">
                {busy ? t(`sso.redirecting.${provider}`) : t(`sso.continueWith.${provider}`)}
              </span>
            </Button>
          )
        })}
      </div>
      {hint !== undefined && <div className="mt-3">{hint}</div>}
    </section>
  )
}

/** The official provider marks, never recoloured (Google forbids a monochrome G). */
function ProviderMark({ provider }: { provider: OAuthProviderId }) {
  if (provider === 'google') {
    return (
      <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0">
        {/* theme-exempt: provider brand mark */}
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
    )
  }
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0">
      {/* theme-exempt: provider brand mark */}
      <path d="M11 11H1V1h10v10z" fill="#F25022" />
      <path d="M23 11H13V1h10v10z" fill="#7FBA00" />
      <path d="M11 23H1V13h10v10z" fill="#00A4EF" />
      <path d="M23 23H13V13h10v10z" fill="#FFB900" />
    </svg>
  )
}
